import type { ReactNode } from 'react';
import {
  Box,
  Button,
  Chip,
  Disclosure,
  Divider,
  EmptyState,
  IconButton,
  Stack,
  ChevronLeftIcon,
  ChevronRightIcon,
  EditIcon,
  citiboxColors,
} from '@citibox-panel/ui';
import type { LockCodeWindow } from '../lib/lockCodeWindow';
import { LockCodeSlot, LOCK_CODE_LABEL_BLOCK_PX } from './LockCodeSlot';

export interface LockCodeRailPanelProps {
  // Id of the unfolded region; the header's `aria-controls` anchor.
  id: string;
  title: string;
  expanded: boolean;
  onToggle: () => void;
  // `null` when this table has no codes at all — a real domain state (a cerradura the CBX10
  // migration left without one of the two tables), not a failure.
  codeWindow: LockCodeWindow | null;
  onSlide: (delta: number) => void;
  onDiscard: () => void;
  // «Confirmar secuencia», handed down as a NODE so nothing between here and the mutation learns
  // about the write. Sits at the foot of the open body, beside the way back when there is one.
  confirmControl?: ReactNode;
  // A write is in flight on one of the carriles. Freezes everything that could move the sequence
  // or the carril out from under it.
  disabled?: boolean;
}

// One «carril»: a code table of the cerradura, folded to its title until the agent asks for it.
// Presentational — it owns no offset and no selection; it renders the window it is handed and
// reports the steps back up.
//
// Folding IS the selection in this design (only one carril unfolds at a time), which is why the
// step controls and the proposal affordances only exist while unfolded: a folded carril has no
// proposal by construction, since putting it away discards the offset.
export function LockCodeRailPanel({
  id,
  title,
  expanded,
  onToggle,
  codeWindow,
  onSlide,
  onDiscard,
  confirmControl,
  disabled = false,
}: LockCodeRailPanelProps) {
  // Signed: negative walks back through the codes given, positive into the ones not given yet.
  const steps = codeWindow?.steps ?? 0;
  const slid = steps !== 0;
  // Nothing to step through on an empty table, and nothing to step through while folded — the
  // controls are laid over the header, so they would otherwise sit on a carril showing no codes.
  const steppable = expanded && codeWindow !== null;
  return (
    <Disclosure
      id={id}
      title={title}
      expanded={expanded}
      onToggle={onToggle}
      draft={slid}
      disabled={disabled}
      // TODO: i18n
      {...(slid
        ? {
            // A capsule and not the status rectangle: this chip does not report the carril's
            // state, it marks the header it sits next to as edited.
            badge: (
              <Chip
                label="PROPUESTA SIN GUARDAR"
                color="warning"
                variant="outlined"
                kind="mark"
                icon={<EditIcon />}
              />
            ),
          }
        : {})}
      {...(steppable
        ? {
            actions: (
              <>
                {/* `stopPropagation` is belt and braces: the façade lays these over the header
                    control rather than inside it, so the click cannot reach the toggle anyway.
                    That same placement is why the freeze has to be combined in by hand: these sit
                    in the disclosure's `actions`, which its own `disabled` deliberately does not
                    reach. Left live during a write, a step repaints the strip and the confirm
                    sentence names a code the request is not carrying — and that sentence is what
                    the agent reads out loud. */}
                <IconButton
                  aria-label="Código anterior"
                  variant="outlined"
                  size="small"
                  stopPropagation
                  disabled={disabled || !codeWindow.canSlideBack}
                  onClick={() => {
                    onSlide(-1);
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label="Código siguiente"
                  variant="outlined"
                  size="small"
                  stopPropagation
                  disabled={disabled || !codeWindow.canSlideForward}
                  onClick={() => {
                    onSlide(1);
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </>
            ),
          }
        : {})}
    >
      {codeWindow === null ? (
        // TODO: i18n
        <EmptyState title="No hay códigos disponibles" />
      ) : (
        <Box>
          {/* The gap is the divider's own air, since the rule is the only thing between the two
              boxes: half a grid step, so it reads as belonging to the pair rather than floating. */}
          <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1 }}>
            {/* The «siguiente» slot takes the larger share: it is the one the agent reads out,
                and the 8-digit reset codes have to sit in it without crowding. */}
            {/* The step count hangs off the label of the slot the codes are moving TOWARDS: walking
                back puts it on «último usado», walking forward on «siguiente». That slot is the one
                whose meaning the slide changes — a «siguiente» two ahead is not the siguiente — and
                the agent has to see it on the label they are about to read out, not somewhere else
                in the carril. Only one of the two can carry it, since a carril has one offset. */}
            <Box sx={{ flex: 2, minWidth: 0 }}>
              <LockCodeSlot
                label="ÚLTIMO USADO"
                code={codeWindow.given[0]?.code ?? null}
                tone="used"
                {...(steps < 0 ? { steps: -steps } : {})}
              />
            </Box>
            {/* Separates the two CODES, not the two columns: it skips the label block and, being
                a stretched flex item, takes exactly what is left — the height of the boxes. Two
                pixels of a darker grey than the card's hairlines, because this one is not chrome:
                it is the boundary between what has been given and what is about to be. */}
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                mt: `${String(LOCK_CODE_LABEL_BLOCK_PX)}px`,
                borderRightWidth: 2,
                borderColor: citiboxColors.grey['400'],
              }}
            />
            <Box sx={{ flex: 3, minWidth: 0 }}>
              <LockCodeSlot
                label="SIGUIENTE"
                code={codeWindow.next?.code ?? null}
                tone="next"
                {...(steps > 0 ? { steps } : {})}
              />
            </Box>
          </Box>
          {/* The foot of the carril: the way back on the left, the action on the right. Unslid
              there is nothing to go back to, so the action sits alone against the right edge. */}
          {(slid || confirmControl !== undefined) && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                mt: 2,
                alignItems: 'center',
                justifyContent: slid ? 'space-between' : 'flex-end',
              }}
            >
              {/* A link and not a pill on purpose: discarding a proposal is the quiet way out of
                  the carril, and giving it button chrome would put it on the same footing as the
                  action the agent actually came for. Frozen mid-write like everything else that
                  could move the sequence — a request already carries the offset it was given. */}
              {/* TODO: i18n */}
              {slid && (
                <Button variant="link" size="small" disabled={disabled} onClick={onDiscard}>
                  Volver al estado actual
                </Button>
              )}
              {confirmControl}
            </Stack>
          )}
        </Box>
      )}
    </Disclosure>
  );
}
